import { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { X } from "lucide-react";
import { dispositivoAttuale } from "@/lib/scaricaApp";
import {
  TASTO_BANNER_APP,
  TESTO_BANNER_APP,
  leggiChiusura,
  mostraBannerApp,
  rigaStore,
  segnaChiusura,
  storeDi,
} from "@/lib/bannerApp";

/**
 * «Hai l'app Datacom AI? Aprila. Non ce l'hai? È gratis.» (23/10/2026)
 * Le regole stanno in src/lib/bannerApp.ts e in DESIGN.md («Il banner dell'app»).
 *
 * PERCHE' NON SI SOVRAPPONE A NIENTE. Molte pagine sono alte esattamente quanto
 * lo schermo (la chat: h-dvh, con la barra di scrittura in fondo). Un banner in
 * piu' sopra le spingerebbe giu' di tanto quanto e' alto lui, e la barra di
 * scrittura finirebbe sotto il bordo. Quindi il banner scrive la sua altezza in
 * `--banner-app` e accende `con-banner-app` su <html>: in index.css quelle
 * pagine diventano alte «schermo meno banner». Chiuso il banner, tutto torna com'era.
 */
export function BannerApp() {
  const { pathname } = useLocation();
  const [chiusoIl, setChiusoIl] = useState(leggiChiusura);
  const fascia = useRef<HTMLDivElement>(null);

  const visibile =
    typeof navigator !== "undefined" &&
    mostraBannerApp({
      nativa: Capacitor.isNativePlatform(),
      userAgent: navigator.userAgent,
      puntiTattili: navigator.maxTouchPoints ?? 0,
      percorso: pathname,
      chiusoIl,
      installataComeSito: typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches,
    });

  useLayoutEffect(() => {
    const radice = document.documentElement;
    const el = fascia.current;
    if (!visibile || !el) return;
    const scrivi = () => radice.style.setProperty("--banner-app", `${el.offsetHeight}px`);
    scrivi();
    radice.classList.add("con-banner-app");
    const osservatore = typeof ResizeObserver === "function" ? new ResizeObserver(scrivi) : null;
    osservatore?.observe(el);
    return () => {
      osservatore?.disconnect();
      radice.classList.remove("con-banner-app");
      radice.style.removeProperty("--banner-app");
    };
  }, [visibile]);

  if (!visibile) return null;
  const dispositivo = dispositivoAttuale();
  const store = storeDi(dispositivo);
  if (!store) return null;

  const chiudi = () => {
    segnaChiusura();
    setChiusoIl(new Date().toISOString());
  };

  return (
    <div
      ref={fascia}
      role="region"
      aria-label="Scarica l'app Datacom AI"
      className="flex items-center gap-3 border-b border-border bg-surface-elevated py-2 pl-3 pr-1 text-foreground"
    >
      <img src="/icon-192.png" alt="" width={36} height={36} className="h-9 w-9 shrink-0 rounded-[10px]" draggable={false} />
      <p className="min-w-0 flex-1 text-sm leading-snug">
        {/* «balance»: due righe pari invece di «È» da solo in fondo (375 px). */}
        <span className="block [text-wrap:balance]">{TESTO_BANNER_APP}</span>
        <span className="block text-xs text-muted-foreground">{rigaStore(dispositivo)}</span>
      </p>
      <a
        href={store}
        className="inline-flex min-h-10 shrink-0 items-center rounded-2xl border border-primary-glow px-4 text-sm font-semibold text-foreground transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {TASTO_BANNER_APP}
      </a>
      <button
        type="button"
        onClick={chiudi}
        aria-label="Chiudi"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}
